import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
    Plus,
    FolderOpen,
    Folder,
    Eye,
    Edit,
    Trash2,
    ChevronDown,
    ChevronRight,
    UserPlus,
    Lock,
    Archive,
    CheckCircle2,
    PauseCircle,
    Search,
    FolderPlus,
} from "lucide-react";

import {
    getRepertoireTreeRequest,
    createRepertoireRequest,
    updateRepertoireRequest,
    deleteRepertoireRequest,
    changeRepertoireStatusRequest,
} from "../../../redux/actions/repertoireActions";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { getAllModulesRequest } from "../../../redux/actions/moduleActions";
import { getActiveRolesRequest, getRolesByModuleRequest } from "../../../redux/actions/roleActions";
import { getAllEntitesRequest } from "../../../redux/actions/entitesActions";
import { getAllJobsRequest } from "../../../redux/actions/Jobactions";

import RepertoireFormModal from "./RepertoireFormModal";
import RepertoireUsersModal from "./RepertoireUsersModal";
import RepertoireDetailsModal from "./RepertoireDetailsModal";
import RepertoireDeleteModal from "./RepertoireDeleteModal";
import { getCurrentRole, isKnowledgeAdminRole, statusLabel } from "../shared/kmsUi";

import "./RepertoireManagement.css";

const STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Actif" },
    { value: "INACTIVE", label: "Inactif" },
    { value: "LOCKED", label: "Verrouillé" },
    { value: "ARCHIVED", label: "Archivé" },
];

const statusBadgeClass = (status) => {
    switch (status) {
        case "ACTIVE":
            return "rep-badge rep-badge--green";
        case "INACTIVE":
            return "rep-badge rep-badge--grey";
        case "LOCKED":
            return "rep-badge rep-badge--blue";
        case "ARCHIVED":
            return "rep-badge rep-badge--red";
        default:
            return "rep-badge rep-badge--grey";
    }
};

const statusIcon = (status) => {
    switch (status) {
        case "ACTIVE":
            return <CheckCircle2 size={12} />;
        case "INACTIVE":
            return <PauseCircle size={12} />;
        case "LOCKED":
            return <Lock size={12} />;
        case "ARCHIVED":
            return <Archive size={12} />;
        default:
            return null;
    }
};

const flattenTree = (nodes = [], level = 0) => {
    let rows = [];
    nodes.forEach((node) => {
        rows.push({ ...node, __level: level });
        if (node.children?.length) {
            rows = rows.concat(flattenTree(node.children, level + 1));
        }
    });
    return rows;
};

const filterTree = (nodes = [], query = "", status = "") =>
    nodes
        .map((node) => {
            const filteredChildren = filterTree(node.children || [], query, status);
            const normalizedQuery = String(query || "").trim().toLowerCase();

            const matchesQuery =
                !normalizedQuery ||
                String(node.name || "").toLowerCase().includes(normalizedQuery) ||
                String(node.code || "").toLowerCase().includes(normalizedQuery) ||
                String(node.description || "").toLowerCase().includes(normalizedQuery) ||
                String(node.path || "").toLowerCase().includes(normalizedQuery);

            const matchesStatus = !status || node.status === status;

            if ((matchesQuery && matchesStatus) || filteredChildren.length > 0) {
                return {
                    ...node,
                    children: filteredChildren,
                };
            }

            return null;
        })
        .filter(Boolean);

function RepertoireRow({
                           node,
                           level,
                           expandedMap,
                           setExpandedMap,
                           onOpenCreateChild,
                           onEdit,
                           onDetails,
                           onDelete,
                           onManageUsers,
                           onChangeStatus,
                           isAdmin,
                       }) {
    const hasChildren = node.children && node.children.length > 0;
    const expanded = Boolean(expandedMap[node.id]);

    const toggleExpand = () => {
        setExpandedMap((prev) => ({
            ...prev,
            [node.id]: !prev[node.id],
        }));
    };

    const permissionedUsersCount = node.userPermissions?.length || node.authorizedUsers?.length || 0;

    return (
        <>
            <tr>
                <td>
                    <div className="rep-tree-cell" style={{ paddingLeft: `${level * 24}px` }}>
                        {hasChildren ? (
                            <button type="button" className="rep-icon-btn" onClick={toggleExpand}>
                                {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            </button>
                        ) : (
                            <span className="rep-tree-spacer" />
                        )}

                        <div className="rep-tree-folder-icon">
                            {expanded ? <FolderOpen size={16} /> : <Folder size={16} />}
                        </div>

                        <div className="rep-tree-main">
                            <div className="rep-tree-name">{node.name}</div>
                            {node.description && <div className="rep-tree-sub">{node.description}</div>}
                        </div>
                    </div>
                </td>

                <td>{node.code}</td>

                <td>
                    <div className="rep-cell-stack">
                        <span className="rep-count-pill">{permissionedUsersCount} </span>
                        <span className="rep-muted">
                            Population: {node.targetUserCount || permissionedUsersCount}
                        </span>
                    </div>
                </td>

                <td>
                    <span className={statusBadgeClass(node.status)}>
                        {statusIcon(node.status)}
                        {statusLabel(node.status)}
                    </span>
                </td>

                <td>{node.childrenCount || 0}</td>
                <td>{node.createdAt ? new Date(node.createdAt).toLocaleDateString("fr-FR") : "-"}</td>

                <td className="is-right">
                    <div className="rep-row-actions">
                        <button
                            type="button"
                            className="rep-icon-btn"
                            title="Voir details"
                            onClick={() => onDetails(node)}
                        >
                            <Eye size={15} />
                        </button>

                        {isAdmin ? (
                            <>
                                <button
                                    type="button"
                                    className="rep-icon-btn"
                                    title="Modifier"
                                    onClick={() => onEdit(node)}
                                >
                                    <Edit size={15} />
                                </button>

                                <button
                                    type="button"
                                    className="rep-icon-btn"
                                    title="Population et permissions"
                                    onClick={() => onManageUsers(node)}
                                >
                                    <UserPlus size={15} />
                                </button>

                                <button
                                    type="button"
                                    className="rep-icon-btn"
                                    title="Ajouter sous-répertoire"
                                    onClick={() => onOpenCreateChild(node)}
                                >
                                    <FolderPlus size={15} />
                                </button>

                                <select
                                    className="rep-status-select"
                                    value={node.status}
                                    onChange={(event) => onChangeStatus(node.id, event.target.value)}
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    type="button"
                                    className="rep-icon-btn rep-icon-btn--danger"
                                    title="Supprimer"
                                    onClick={() => onDelete(node)}
                                >
                                    <Trash2 size={15} />
                                </button>
                            </>
                        ) : null}
                    </div>
                </td>
            </tr>

            {hasChildren &&
                expanded &&
                node.children.map((child) => (
                    <RepertoireRow
                        key={child.id}
                        node={child}
                        level={level + 1}
                        expandedMap={expandedMap}
                        setExpandedMap={setExpandedMap}
                        onOpenCreateChild={onOpenCreateChild}
                        onEdit={onEdit}
                        onDetails={onDetails}
                        onDelete={onDelete}
                        onManageUsers={onManageUsers}
                        onChangeStatus={onChangeStatus}
                        isAdmin={isAdmin}
                    />
                ))}
        </>
    );
}

function RepertoireManagement({
                                  repertoireTree,
                                  loading,
                                  allUsers,
                                  entites,
                                  jobs,
                                  modules,
                                  roles,
                                  getTree,
                                  getAllUsers,
                                  getAllEntites,
                                  getAllJobs,
                                  getAllModules,
                                  getActiveRoles,
                                  getRolesByModule,
                                  createRepertoire,
                                  updateRepertoire,
                                  deleteRepertoire,
                                  changeStatus,
                                  currentRole,
                              }) {
    const isAdmin = isKnowledgeAdminRole(currentRole);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [expandedMap, setExpandedMap] = useState({});
    const [formOpen, setFormOpen] = useState(false);
    const [usersOpen, setUsersOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [parentItem, setParentItem] = useState(null);

    useEffect(() => {
        getTree();
        getAllUsers();
        getAllEntites();
        getAllJobs();
        getAllModules();
        getActiveRoles();
    }, [getTree, getAllUsers, getAllEntites, getAllJobs, getAllModules, getActiveRoles]);

    useEffect(() => {
        if (!modules || modules.length === 0) return;

        const kmsModule = modules.find((module) =>
            String(module.code || "").toUpperCase().includes("KMS")
        );

        if (kmsModule) {
            getRolesByModule(kmsModule.id);
        }
    }, [modules, getRolesByModule]);

    const filteredTree = useMemo(
        () => filterTree(repertoireTree || [], search, statusFilter),
        [repertoireTree, search, statusFilter]
    );

    const flatTree = useMemo(() => flattenTree(repertoireTree || []), [repertoireTree]);

    const handleAddRoot = () => {
        setEditingItem(null);
        setParentItem(null);
        setFormOpen(true);
    };

    const handleAddChild = (parent) => {
        setEditingItem(null);
        setParentItem(parent);
        setFormOpen(true);
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setParentItem(null);
        setFormOpen(true);
    };

    const handleDetails = (item) => {
        setSelectedItem(item);
        setDetailsOpen(true);
    };

    const handleDelete = (item) => {
        setSelectedItem(item);
        setDeleteOpen(true);
    };

    const handleManageUsers = (item) => {
        setSelectedItem(item);
        setUsersOpen(true);
    };

    const handleSubmitForm = (payload) => {
        if (editingItem) {
            updateRepertoire(editingItem.id, payload);
        } else {
            createRepertoire(payload);
        }

        setFormOpen(false);
        setEditingItem(null);
        setParentItem(null);
    };

    const handleConfirmDelete = () => {
        if (!selectedItem) return;
        deleteRepertoire(selectedItem.id);
        setDeleteOpen(false);
        setSelectedItem(null);
    };

    const handleSaveUsers = (payload) => {
        if (!selectedItem) return;

        updateRepertoire(selectedItem.id, {
            code: selectedItem.code,
            name: selectedItem.name,
            description: selectedItem.description,
            path: selectedItem.path,
            roleId: selectedItem.roleId,
            parentId: selectedItem.parentId,
            status: selectedItem.status,
            ...payload,
        });

        setUsersOpen(false);
        setSelectedItem(null);
    };

    return (
        <div className="rep-page">
            <div className="rep-header">
                <div>
                    <h1>Répertoires</h1>
                </div>

                {isAdmin ? <div className="rep-header-actions">
                    <button type="button" className="rep-btn rep-btn--primary" onClick={handleAddRoot}>
                        <Plus size={15} />
                        Ajouter un répertoire
                    </button>
                </div> : null}
            </div>

            <div className="rep-toolbar">
                <div className="rep-search-wrap">
                    <Search size={14} className="rep-search-icon" />
                    <input
                        type="text"
                        className="rep-search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Nom, code, description, chemin..."
                    />
                </div>

                <div className="rep-filter-tabs">
                    <select
                        className="rep-toolbar-select"
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value)}
                    >
                        <option value="">Tous les statuts</option>
                        {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="rep-meta">{loading ? "..." : `${flatTree.length} répertoire(s)`}</div>
            </div>

            <div className="rep-wrap">
                <div className="rep-card">
                    <div className="rep-body">
                        <div className="rep-table-wrap">
                            <table className="rep-table">
                                <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Code</th>
                                    <th>Population / permissions</th>
                                    <th>Statut</th>
                                    <th>Enfants</th>
                                    <th>Créé le</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>

                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className="rep-loading">Chargement...</div>
                                        </td>
                                    </tr>
                                ) : filteredTree.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="rep-empty-cell">
                                            Aucun répertoire trouvé
                                        </td>
                                    </tr>
                                ) : (
                                    filteredTree.map((node) => (
                                        <RepertoireRow
                                            key={node.id}
                                            node={node}
                                            level={0}
                                            expandedMap={expandedMap}
                                            setExpandedMap={setExpandedMap}
                                            onOpenCreateChild={handleAddChild}
                                            onEdit={handleEdit}
                                            onDetails={handleDetails}
                                            onDelete={handleDelete}
                                            onManageUsers={handleManageUsers}
                                            onChangeStatus={changeStatus}
                                            isAdmin={isAdmin}
                                        />
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <RepertoireFormModal
                open={formOpen}
                onClose={() => {
                    setFormOpen(false);
                    setEditingItem(null);
                    setParentItem(null);
                }}
                onSubmit={handleSubmitForm}
                editingItem={editingItem}
                parentItem={parentItem}
                allUsers={allUsers}
                allRoles={roles}
                allEntites={entites}
                allJobs={jobs}
                flatTree={flatTree}
                loading={loading}
            />

            <RepertoireUsersModal
                open={usersOpen}
                onClose={() => {
                    setUsersOpen(false);
                    setSelectedItem(null);
                }}
                repertoire={selectedItem}
                allUsers={allUsers}
                allEntites={entites}
                allJobs={jobs}
                onSave={handleSaveUsers}
            />

            <RepertoireDetailsModal
                open={detailsOpen}
                onClose={() => {
                    setDetailsOpen(false);
                    setSelectedItem(null);
                }}
                repertoire={selectedItem}
            />

            <RepertoireDeleteModal
                open={deleteOpen}
                onClose={() => {
                    setDeleteOpen(false);
                    setSelectedItem(null);
                }}
                repertoire={selectedItem}
                onConfirm={handleConfirmDelete}
                loading={loading}
            />
        </div>
    );
}

const mapStateToProps = (state) => ({
    repertoireTree: state.repertoire?.repertoireTree || [],
    loading: state.repertoire?.loading,
    allUsers: state.user?.users || [],
    entites: state.entite?.entites || [],
    jobs: state.jobs?.jobs || [],
    modules: state.module?.modules || [],
    roles: state.role?.roles || [],
    currentRole: getCurrentRole(state),
});

const mapDispatchToProps = (dispatch) => ({
    getTree: () => dispatch(getRepertoireTreeRequest()),
    getAllUsers: () => dispatch(getAllUsersRequest()),
    getAllEntites: () => dispatch(getAllEntitesRequest()),
    getAllJobs: () => dispatch(getAllJobsRequest()),
    getActiveRoles: () => dispatch(getActiveRolesRequest()),
    getAllModules: () => dispatch(getAllModulesRequest()),
    getRolesByModule: (moduleId) => dispatch(getRolesByModuleRequest(moduleId)),
    createRepertoire: (data) => dispatch(createRepertoireRequest(data)),
    updateRepertoire: (id, data) => dispatch(updateRepertoireRequest(id, data)),
    deleteRepertoire: (id) => dispatch(deleteRepertoireRequest(id)),
    changeStatus: (id, status) => dispatch(changeRepertoireStatusRequest(id, status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RepertoireManagement);

